import {fetch, post, put, remove} from '@/base-http'
import Category from '@/modules/support/model/Category'
import Pagination, {PaginationInput} from '@/modules/core/model/pagination'
import Guide from '@/modules/support/model/Guide'
import KeyValuePair from '@/modules/core/support/KeyValuePair'
import GuideView from '@/modules/support/model/GuideView'
import {Screen} from "@/modules/support/model/Screen";

/**
 * Filtros de búsqueda de guías
 */
export interface GuideListFilters {
    title?: string
    categories?: number[]
}

export interface GuideViewListFilters {
    users?: number[]
    guides?: number[]
}

/**
 * Registra una nueva categoría en el servidor
 *
 * @param category
 */
export async function createCategory(category: Category) {
    if (!category.isNew) {
        throw new Error('La categoría ya posee un id, se puede volver a registrar')
    }

    const result = await post('support/guide/category', category.serialize())

    return Category.deserialize(result)
}

/**
 * Permite obtener una categoría de guías por el id argumentado
 *
 * @param id
 */
export async function getCategory(id: number) {
    const data = await fetch(`support/guide/category/${id}`)

    return Category.deserialize(data)
}

/**
 * Permite obtener una lista de guías
 *
 * @param options
 */
export async function getCategoryList(options: PaginationInput<GuideListFilters>): Promise<Pagination<Guide>> {
    const filters = options.filters || {}
    const params: KeyValuePair = {}
    if (filters.title) {
        params.title = filters.title
    }

    if (filters.categories) {
        params.categories = JSON.stringify(filters.categories)
    }

    if (options.page) {
        params.page = options.page
    }

    if (options.recordsPerPage) {
        params.recordsPerPage = options.recordsPerPage
    }

    const data = await fetch('support/guide/guides', {params})

    return {
        items: data.records.map((record: KeyValuePair) => Guide.deserialize(record)),
        totalRecords: data.total_records,
        totalPages: 0,
    }
}

/**
 * Permite obtener un guía por id
 *
 * @param id
 */
export async function getGuide(id: number) {
    const data = await fetch(`support/guide/guide/${id}`)

    return Guide.deserialize(data)
}

/**
 * Actualiza la información de un categoría en el servidor
 *
 * @param category
 */
export async function updateCategory(category: Category) {
    if (category.isNew) {
        throw new Error('No se puede actualizar la información de una categoría que no tiene ID')
    }
    const data = await put(`support/guide/category/${category.id}`, category.serialize())

    return Category.deserialize(data)
}

/**
 * Registra una nueva en el servidor
 *
 * @param guide
 */
export async function crateGuide(guide: Guide) {
    if (!guide.isNew) {
        throw new Error(`No se crear la guía ${guide.id} porque ya existe en el servidor`)
    }

    const data = await post('support/guide/guide', guide.serialize())

    return Guide.deserialize(data)
}

/**
 * Registra una nueva en el servidor
 *
 * @param guide
 */
export async function updateGuide(guide: Guide) {
    if (guide.isNew) {
        throw new Error(`No se actualizar la guía "${guide.title}" porque aún no se ha registro en el servidor`)
    }

    const data = await put(`support/guide/guide/${guide.id}`, guide.serialize())

    return Guide.deserialize(data)
}

/**
 * Elimina un guía
 *
 * @param id
 */
export async function deleteGuide(id: number): Promise<boolean> {
    const result = await remove(`support/guide/guide/${id}`)

    return result.result || false
}

/**
 * Permite obtener una lista de guías
 *
 * @param options
 */
export async function getGuideViewList(options: PaginationInput<GuideViewListFilters>): Promise<Pagination<GuideView>> {
    const filters = options.filters || {}
    const params: KeyValuePair = {}

    if (filters.guides) {
        params.guides = JSON.stringify(filters.guides)
    }
    if (filters.users) {
        params.users = JSON.stringify(filters.users)
    }
    if (options.page) {
        params.page = options.page
    }
    if (options.recordsPerPage) {
        params.recordsPerPage = options.recordsPerPage
    }

    const data = await fetch('support/guide/guide-view-history', {params})

    return {
        items: data.records.map((record: KeyValuePair) => GuideView.deserialize(record)),
        totalRecords: data.total_records,
        totalPages: 0,
    }
}

/**
 * Elimina una categoría en el servidor
 *
 * @param id
 * @param options
 */
export async function deleteCategory(id: number, options = {replacementCategory: 0}) {
    const result = await remove(`support/guide/category/${id}`, {
        params: {
            reassignTo: options.replacementCategory || undefined,
        },
    })

    return result.result || false
}

/**
 * Permite obtener la lista de pantallas
 */
export async function getScreenList (): Promise<Screen[]> {
    const data = await fetch('support/guide/screens')

    if (!Array.isArray(data)) {
        throw new Error('La lista de pantallas no es válida')
    }

    return data.map((s: KeyValuePair) => Object.assign(new Screen(), s))
}
